import type { Workspace } from "#app/lib/data/workspace-context";
import type { WorkspaceRecents } from "#app/lib/workspace-recents";

/**
 * Above this many workspaces the picker heads its card with a search row and
 * labels its sections. Five rows still read at a glance on one screen; past
 * that, scanning starts to cost more than typing. Both are finding aids, so
 * they arrive together — a search over a short list is chrome for nothing,
 * and "Recent" / "Everything else" around two names is furniture.
 */
const WORKSPACE_SEARCH_THRESHOLD = 5;

export function showsWorkspaceSearch(count: number): boolean {
  return count > WORKSPACE_SEARCH_THRESHOLD;
}

/**
 * How many workspaces the picker's Recent section holds. Three is what a
 * person actually alternates between; a fourth is already "the one I opened
 * last month", which the A–Z answers just as well.
 */
const PICKER_RECENT_LIMIT = 3;

/**
 * The switcher menu's order: rows awaiting a decision first (an invite), then
 * the workspaces opened on this device, most recent first, then everything the
 * viewer has never opened here, alphabetically. Stable within each band.
 */
export function orderWorkspaces(
  workspaces: readonly Workspace[],
  recents: WorkspaceRecents,
): Workspace[] {
  const band = (w: Workspace): number => {
    if (w.memberStatus === "invited") return 0;
    return recents[w.workspaceId] ? 1 : 2;
  };
  return [...workspaces].sort((a, b) => {
    const bandDiff = band(a) - band(b);
    if (bandDiff !== 0) return bandDiff;
    if (band(a) === 1) {
      const byRecent = (recents[b.workspaceId] ?? "").localeCompare(
        recents[a.workspaceId] ?? "",
      );
      if (byRecent !== 0) return byRecent;
    }
    return byName(a, b);
  });
}

function byName(a: Workspace, b: Workspace): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

/**
 * The rows the picker shows. A workspace the viewer left is theirs no more —
 * it leaves the switcher the way it left their access — and nothing on its
 * row could be offered (the way back is an admin's fresh invite, which
 * arrives as an `invited` row).
 */
export function pickerWorkspaces(
  workspaces: readonly Workspace[],
): Workspace[] {
  return workspaces.filter((w) => w.memberStatus !== "left");
}

type WorkspaceSectionKey =
  | "invitations"
  | "recent"
  | "other"
  | "pendingDeletion";

export type WorkspaceSection = {
  key: WorkspaceSectionKey;
  label: string;
  workspaces: Workspace[];
};

const SECTION_LABEL: Record<WorkspaceSectionKey, string> = {
  invitations: "Invitations",
  recent: "Recent",
  other: "Everything else",
  pendingDeletion: "Pending deletion",
};

/**
 * Whether an invite row can still be taken up. A declined invite keeps its
 * Join: the accept mutation flips any membership row to active. Expired and
 * suspended rows have no self-serve way back in and list as plain rows.
 */
export function isPendingInvitation(workspace: Workspace): boolean {
  return (
    workspace.memberStatus === "invited" || workspace.memberStatus === "declined"
  );
}

/**
 * The picker's list, in the order a reader decides in:
 *
 *  1. Invitations — decisions, so they lead.
 *  2. Recent — what THIS device opened, newest first, capped at
 *     `PICKER_RECENT_LIMIT`.
 *  3. Everything else — A–Z. A recent is not repeated here.
 *  4. Pending deletion — still openable (the pending screen, where an Owner
 *     restores), but not a place to work, so it sinks out of the A–Z whatever
 *     its recency; soonest deletion first.
 *
 * Empty sections are dropped. The ORDER is always kept; whether the labels and
 * dividers render is the caller's call (the picker draws them only past
 * `WORKSPACE_SEARCH_THRESHOLD`, and never for a lone section).
 */
export function groupWorkspaces(
  workspaces: readonly Workspace[],
  recents: WorkspaceRecents,
): WorkspaceSection[] {
  const invitations: Workspace[] = [];
  const pendingDeletion: Workspace[] = [];
  const opened: Workspace[] = [];
  const rest: Workspace[] = [];

  for (const w of workspaces) {
    if (isPendingInvitation(w)) invitations.push(w);
    else if (w.deletion) pendingDeletion.push(w);
    else if (recents[w.workspaceId]) opened.push(w);
    else rest.push(w);
  }

  opened.sort((a, b) =>
    (recents[b.workspaceId] ?? "").localeCompare(recents[a.workspaceId] ?? ""),
  );
  const recent = opened.slice(0, PICKER_RECENT_LIMIT);
  const other = [...rest, ...opened.slice(PICKER_RECENT_LIMIT)].sort(byName);
  invitations.sort(byName);
  pendingDeletion.sort(
    (a, b) =>
      (a.deletion?.scheduledAt ?? "").localeCompare(
        b.deletion?.scheduledAt ?? "",
      ) || byName(a, b),
  );

  const sections: WorkspaceSection[] = [
    { key: "invitations", label: SECTION_LABEL.invitations, workspaces: invitations },
    { key: "recent", label: SECTION_LABEL.recent, workspaces: recent },
    { key: "other", label: SECTION_LABEL.other, workspaces: other },
    {
      key: "pendingDeletion",
      label: SECTION_LABEL.pendingDeletion,
      workspaces: pendingDeletion,
    },
  ];
  return sections.filter((s) => s.workspaces.length > 0);
}

/** Case- and whitespace-insensitive substring match on the name. */
export function filterWorkspaces(
  workspaces: readonly Workspace[],
  query: string,
): Workspace[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...workspaces];
  return workspaces.filter((w) => w.name.toLowerCase().includes(needle));
}

/**
 * A query narrows every section in place: the survivors keep their section
 * and its order, and a section that loses every row drops out.
 */
export function filterSections(
  sections: readonly WorkspaceSection[],
  query: string,
): WorkspaceSection[] {
  return sections
    .map((s) => ({ ...s, workspaces: filterWorkspaces(s.workspaces, query) }))
    .filter((s) => s.workspaces.length > 0);
}
